/* ==========================================================================
   Noble Sliding Review — auto-scroll web component
   --------------------------------------------------------------------------
   Why the old marquee broke (and how this fixes it):
   - A CSS @keyframes translateX(-50%) marquee only loops seamlessly if the
     track is EXACTLY duplicated and every card is the same fixed width. Any
     missing clone, odd item count, or late-loading media throws the offset
     off and the loop visibly jumps or stalls.
   - This version measures the real width of one "set" of cards, clones the
     set enough times to fill the viewport, and animates with
     requestAnimationFrame. When the offset passes one set width it wraps by
     adding the set width back — perfectly seamless, framerate-independent,
     and self-correcting on resize.
   ========================================================================== */

(function () {
  'use strict';

  var MOBILE_BREAKPOINT = 750;
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class NobleSlidingReview extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.viewport = this.querySelector('.noble-sliding-review__viewport');
      this.track = this.querySelector('.noble-sliding-review__track');
      if (!this.viewport || !this.track) return;

      this.originals = Array.prototype.slice.call(this.track.children);
      if (!this.originals.length) return;

      this.speed = parseFloat(this.dataset.speed) || 30;     // seconds per full set
      this.autoDesktop = this.dataset.autoSlideDesktop === 'true';
      this.autoMobile = this.dataset.autoSlideMobile === 'true';

      this.offset = 0;
      this.setWidth = 0;
      this.playing = false;
      this.hovered = false;
      this.dragging = false;
      this.inView = true;
      this.tabVisible = !document.hidden;
      this.soundActive = false;

      // bind handlers once
      this.tick = this.tick.bind(this);
      this.onResize = this.debounce(this.layout.bind(this), 200);

      this.layout();
      this.bindInteractions();
      this.setupVideos();
      this.observeVisibility();

      window.addEventListener('resize', this.onResize, { passive: true });
      document.addEventListener('visibilitychange', () => {
        this.tabVisible = !document.hidden;
        this.evaluatePlay();
      });
      reduceMotion.addEventListener &&
        reduceMotion.addEventListener('change', () => this.evaluatePlay());

      this.raf = requestAnimationFrame(this.tick);
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.raf);
      window.removeEventListener('resize', this.onResize);
    }

    /* ----------------------------------------------------------------------
       Layout: remove old clones, measure one set, clone to fill viewport.
       ---------------------------------------------------------------------- */
    layout() {
      // strip previously generated clones
      Array.prototype.slice.call(this.track.querySelectorAll('[data-nsr-clone]'))
        .forEach(function (n) { n.remove(); });

      var style = getComputedStyle(this.track);
      var gap = parseFloat(style.columnGap || style.gap) || 0;

      // width of one full set = sum(card widths) + one gap per card
      // (the trailing gap accounts for the gap between repeated sets)
      var setWidth = 0;
      this.originals.forEach(function (el) {
        setWidth += el.getBoundingClientRect().width + gap;
      });
      this.setWidth = setWidth;

      if (!setWidth) return;

      // clone whole sets until the track is at least viewport + 2 sets wide,
      // guaranteeing content on both sides of any wrap point
      var target = this.viewport.clientWidth + setWidth * 2;
      var self = this;
      var guard = 0;
      while (this.track.scrollWidth < target && guard < 40) {
        this.originals.forEach(function (el) {
          var clone = el.cloneNode(true);
          clone.setAttribute('data-nsr-clone', '');
          clone.setAttribute('aria-hidden', 'true');
          self.track.appendChild(clone);
        });
        guard++;
      }

      // re-wire any cloned video cards
      this.setupVideos();

      // keep current offset within range
      if (this.offset <= -this.setWidth) {
        this.offset = this.offset % this.setWidth;
      }
      this.track.style.transform = 'translate3d(' + this.offset + 'px,0,0)';
      this.evaluatePlay();
    }

    /* ----------------------------------------------------------------------
       Should we be auto-scrolling right now?
       ---------------------------------------------------------------------- */
    autoEnabledForViewport() {
      var isMobile = window.innerWidth < MOBILE_BREAKPOINT;
      return isMobile ? this.autoMobile : this.autoDesktop;
    }

    evaluatePlay() {
      this.playing =
        this.autoEnabledForViewport() &&
        !reduceMotion.matches &&
        !this.hovered &&
        !this.dragging &&
        !this.soundActive &&
        this.inView &&
        this.tabVisible;
      // reset the frame clock so we don't jump after a pause
      this._last = 0;
    }

    /* ----------------------------------------------------------------------
       Animation loop
       ---------------------------------------------------------------------- */
    tick(now) {
      if (!this._last) this._last = now;
      var dt = (now - this._last) / 1000;
      this._last = now;

      if (this.playing && this.setWidth) {
        var pps = this.setWidth / this.speed; // pixels per second
        this.offset -= pps * Math.min(dt, 0.05); // clamp dt after tab refocus
        if (this.offset <= -this.setWidth) this.offset += this.setWidth;
        if (this.offset > 0) this.offset -= this.setWidth;
        this.track.style.transform = 'translate3d(' + this.offset + 'px,0,0)';
      }
      this.raf = requestAnimationFrame(this.tick);
    }

    /* ----------------------------------------------------------------------
       Interactions: hover pause + pointer drag
       ---------------------------------------------------------------------- */
    bindInteractions() {
      var self = this;
      var pauseHover = this.dataset.pauseOnHover !== 'false';

      if (pauseHover) {
        this.addEventListener('mouseenter', function () {
          self.hovered = true; self.evaluatePlay();
        });
        this.addEventListener('mouseleave', function () {
          self.hovered = false; self.evaluatePlay();
        });
      }

      // pointer drag to scrub
      var startX = 0, startOffset = 0, moved = 0, pointerId = null;

      this.viewport.addEventListener('pointerdown', function (e) {
        if (e.button === 1 || e.button === 2) return;
        pointerId = e.pointerId;
        self.dragging = true;
        moved = 0;
        startX = e.clientX;
        startOffset = self.offset;
        self.setAttribute('data-grabbing', 'true');
        self.evaluatePlay();
      });

      window.addEventListener('pointermove', function (e) {
        if (!self.dragging || e.pointerId !== pointerId) return;
        var dx = e.clientX - startX;
        moved = Math.abs(dx);
        var next = startOffset + dx;
        // wrap during drag too
        if (self.setWidth) {
          next = next % self.setWidth;
          if (next > 0) next -= self.setWidth;
        }
        self.offset = next;
        self.track.style.transform = 'translate3d(' + self.offset + 'px,0,0)';
      });

      var endDrag = function () {
        if (!self.dragging) return;
        self.dragging = false;
        self.removeAttribute('data-grabbing');
        self.evaluatePlay();
      };
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);

      // swallow click after a real drag so card links don't fire
      this.viewport.addEventListener('click', function (e) {
        if (moved > 6) { e.preventDefault(); e.stopPropagation(); }
      }, true);
    }

    /* ----------------------------------------------------------------------
       Lazy video loading + sound toggle
       ---------------------------------------------------------------------- */
    setupVideos() {
      var self = this;
      var cards = this.track.querySelectorAll('.noble-video-card');

      if (!this._videoObserver && 'IntersectionObserver' in window) {
        this._videoObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            var video = entry.target;
            if (entry.isIntersecting) {
              self.loadVideo(video);
              video.play && video.play().catch(function () {});
            } else if (video.pause) {
              video.pause();
            }
          });
        }, { root: null, rootMargin: '200px', threshold: 0.01 });
      }

      Array.prototype.forEach.call(cards, function (card) {
        var video = card.querySelector('.noble-lazy-review-video');
        var toggle = card.querySelector('.noble-video-sound-toggle');

        if (video && !video._nsrInit) {
          video._nsrInit = true;
          video.muted = true;
          video.setAttribute('muted', '');
          card.setAttribute('data-muted', 'true');
          video.addEventListener('playing', function () {
            card.setAttribute('data-playing', 'true');
          });
          if (self._videoObserver) self._videoObserver.observe(video);
        }

        if (toggle && !toggle._nsrInit) {
          toggle._nsrInit = true;
          toggle.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();
            self.toggleSound(card);
          });
        }
      });
    }

    loadVideo(video) {
      if (video._loaded) return;
      var sources = video.querySelectorAll('source[data-video-source]');
      if (!sources.length) return;
      Array.prototype.forEach.call(sources, function (s) {
        if (!s.src) s.src = s.getAttribute('data-video-source');
      });
      video._loaded = true;
      video.load();
    }

    toggleSound(card) {
      var video = card.querySelector('.noble-lazy-review-video');
      if (!video) return;
      var willUnmute = video.muted;

      // mute every other video in this section first
      var all = this.track.querySelectorAll('.noble-lazy-review-video');
      Array.prototype.forEach.call(all, function (v) {
        var c = v.closest('.noble-video-card');
        v.muted = true;
        if (c) c.setAttribute('data-muted', 'true');
      });

      if (willUnmute) {
        this.loadVideo(video);
        video.muted = false;
        card.setAttribute('data-muted', 'false');
        video.play && video.play().catch(function () {});
        this.soundActive = true; // pause the rail so it can be watched
      } else {
        this.soundActive = false;
      }
      this.evaluatePlay();
    }

    /* ----------------------------------------------------------------------
       Pause when the whole section scrolls out of view
       ---------------------------------------------------------------------- */
    observeVisibility() {
      var self = this;
      if (!('IntersectionObserver' in window)) return;
      this._sectionObserver = new IntersectionObserver(function (entries) {
        self.inView = entries[0].isIntersecting;
        self.evaluatePlay();
      }, { threshold: 0 });
      this._sectionObserver.observe(this);
    }

    /* ---------------------------------------------------------------------- */
    debounce(fn, wait) {
      var t;
      return function () {
        var ctx = this, args = arguments;
        clearTimeout(t);
        t = setTimeout(function () { fn.apply(ctx, args); }, wait);
      };
    }
  }

  if (!customElements.get('noble-sliding-review')) {
    customElements.define('noble-sliding-review', NobleSlidingReview);
  }
})();